import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import ContentWrapper from 'components/content-wrapper';
import Heading from 'components/heading';
import Text from 'components/text';
import Link from 'components/link';
import Icon from 'components/icon';
import Image from 'components/image';

import useFormatDate from 'hooks/use-format-date';
import useKnipsPostFormatter from 'hooks/use-knips-post-formatter';

const KnipsFeedBlock = ({ apiUrl, title, allUrl, theme }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!apiUrl) return;

    setLoading(true);

    fetch(apiUrl)
      .then(res => {
        if (!res.ok) throw new Error('Network response was not ok');
        return res.json();
      })
      .then(data => {
        const formattedPosts = useKnipsPostFormatter(data?.items);
        setPosts(formattedPosts);
      })
      .catch(err => {
        setPosts([]);
      })
      .finally(() => setLoading(false));
  }, [apiUrl]);

  return (
    <div
      className={cn('knips-feed-block', {
        [`knips-feed-block--${theme}`]: theme
      })}
    >
      <ContentWrapper className="knips-feed-block__wrapper">
        <div className="knips-feed-block__header">
          {title && (
            <div className="knips-feed-block__header-title">
              <Heading theme={Heading.themes.h2Hero}>{title}</Heading>
            </div>
          )}
          {allUrl && (
            <div className="knips-feed-block__header-link">
              <Link
                url={allUrl}
                text="Se alle nyheter"
                showIcon={true}
                iconIsAfterText={true}
                iconName="external-link-small"
                theme={Link.themes.external}
              />
            </div>
          )}
        </div>
        {loading ? (
          <div className="knips-feed-block__loading" aria-live="polite">
            Laster innhold…
          </div>
        ) : posts?.length > 0 ? (
          <ul className="knips-feed-block__grid">
            {posts.map(({ title, text, id, url, publishDate, imageSrc }) => {
              return (
                <li key={id} className="knips-feed-block__card-item">
                  <div className="knips-feed-block__card">
                    {imageSrc && (
                      <div className="knips-feed-block__card-image">
                        <Image src={imageSrc} alt="" />
                      </div>
                    )}
                    <div className="knips-feed-block__card-body">
                      <span className="knips-feed-block__meta">
                        {useFormatDate(publishDate)}
                      </span>
                      {title && (
                        <div className="knips-feed-block__card-title">
                          <Heading
                            theme={Heading.themes.h3}
                            className="knips-feed-block__card-title-text"
                            level={3}
                            color={
                              theme === 'dark'
                                ? Link.colors.beige
                                : Link.colors.greenDark
                            }
                          >
                            <a
                              href={url}
                              className="knips-feed-block__card-title-link"
                            >
                              {title}
                              <Icon
                                name="external-link-small"
                                className="knips-feed-block__card-title-icon"
                                aria-label="Ekstern lenke"
                              />
                            </a>
                          </Heading>
                        </div>
                      )}
                      {text && (
                        <div className="knips-feed-block__card-excerpt">
                          <Text
                            className="knips-feed-block__card-excerpt-text"
                            theme={Text.themes.body}
                          >
                            {text}
                          </Text>
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <div
            className="knips-feed-block__empty"
            role="status"
            aria-live="polite"
          >
            Fant ingen nyheter.
          </div>
        )}
      </ContentWrapper>
    </div>
  );
};

KnipsFeedBlock.propTypes = {
  apiUrl: PropTypes.string.isRequired,
  title: PropTypes.string,
  allUrl: PropTypes.string,
  theme: PropTypes.oneOf(['light', 'dark'])
};

KnipsFeedBlock.propTypesMeta = {
  theme: 'exclude'
};

KnipsFeedBlock.themes = {
  light: 'light',
  dark: 'dark'
};

export default KnipsFeedBlock;
